import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, RefreshCw, Search, Filter, Download, ChevronDown, AlertCircle, Menu, AlertTriangle, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from './lib/supabase';
import { subscribeToAuthChanges } from './lib/auth';
import { Login } from './pages/Login';
import { User } from 'firebase/auth';
import { Sidebar } from './components/Sidebar';
import { useStore } from './lib/store';
import { resolveEnrollment, markAsError } from './lib/api';
import { Notifications } from './components/Notifications';
import { exportToCSV } from './utils/export';
import { useVerification } from './hooks/useVerification';
import { toast } from 'react-hot-toast';

type Student = {
  id: string;
  nome_completo: string;
  email: string;
  cpf: string;
  rg: string;
  orgao_expedidor: string;
  data_de_nascimento: string;
  numero_da_residencia: string;
  endereco: string;
  complemento: string;
  cep: string;
  cidade: string;
  estado: string;
  bairro: string;
  cod_curso: string;
  cod_curso_2: string;
  metodo_de_pagamento: string;
  parcelas: number;
  celular: string;
  data: string;
  status: 'success' | 'error';
  motivo_do_erro?: string | null;
  originalTable?: string;
  verified: boolean;
};

type TableInfo = {
  table_name: string;
  table_type: 'success' | 'error' | 'other';
};

type VerificationFilter = 'all' | 'verified' | 'unverified';

type SortOrder = 'recent' | 'oldest' | 'alphabetical';

const ITEMS_PER_PAGE = 10;

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [activeTab, setActiveTab] = useState<'success' | 'error'>('success');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStudent, setExpandedStudent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [menuOpen, setMenuOpen] = useState(true);
  const [allErrorEmails, setAllErrorEmails] = useState<string[]>([]);
  const [targetErrorId, setTargetErrorId] = useState<string | null>(null);
  const [verificationFilter, setVerificationFilter] = useState<VerificationFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const { courses, fetchCourses } = useStore();
  const { 
    verifiedStudents, 
    loadVerificationStatus, 
    handleVerification, 
    isVerified 
  } = useVerification();

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      setUser(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    console.log('Iniciando carregamento de dados...');
    fetchTables();
    fetchCourses().then(() => {
      console.log('Cursos carregados:', courses);
    });
  }, [user, fetchCourses]);

  useEffect(() => {
    if (tables.length > 0) {
      fetchStudents();
      if (targetErrorId && activeTab === 'error') {
        setExpandedStudent(targetErrorId);
        const element = document.getElementById(`student-${targetErrorId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTargetErrorId(null);
      }
    }
  }, [activeTab, tables, targetErrorId]);

  useEffect(() => {
    loadVerificationStatus();
  }, [loadVerificationStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, verificationFilter, sortOrder, activeTab]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortStudents = (studentsToSort: Student[]) => {
    return [...studentsToSort].sort((a, b) => {
      switch (sortOrder) {
        case 'recent':
          return new Date(b.data || 0).getTime() - new Date(a.data || 0).getTime();
        case 'oldest':
          return new Date(a.data || 0).getTime() - new Date(b.data || 0).getTime();
        case 'alphabetical':
          return (a.nome_completo || '').localeCompare(b.nome_completo || '');
        default:
          return 0;
      }
    });
  };

  const findErrorStudentByEmail = (email: string) => {
    const errorStudent = students.find(s => s.status === 'error' && s.email === email);
    return errorStudent?.id;
  };

  const fetchTables = async () => {
    try {
      console.log('Iniciando busca de tabelas...');
      
      const { data, error } = await supabase.rpc('list_tables');
      
      if (error) {
        console.error('Erro ao buscar lista de tabelas:', error);
        console.log('Detalhes do erro:', {
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return;
      }

      if (data) {
        console.log('Tabelas encontradas (raw):', data);
        const formattedTables = data.map((table: { table_name: string }) => ({
          table_name: table.table_name,
          table_type: table.table_name.startsWith('cad_') ? 'success' : 
                     table.table_name.startsWith('falha_') ? 'error' : 
                     'other'
        }));
        console.log('Tabelas formatadas:', formattedTables);
        setTables(formattedTables);
      } else {
        console.log('Nenhuma tabela encontrada');
      }
    } catch (error) {
      console.error('Erro ao buscar lista de tabelas:', error);
      if (error instanceof Error) {
        console.log('Detalhes do erro:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      console.log('Iniciando busca de dados...');
      console.log('Tab ativo:', activeTab);
      console.log('Tabelas disponíveis:', tables);

      const errorTables = tables.filter(t => t.table_type === 'error');
      let errorEmails: string[] = [];
      
      for (const table of errorTables) {
        const { data: errorData } = await supabase
          .from(table.table_name)
          .select('email');
        
        if (errorData) {
          errorEmails = [...errorEmails, ...errorData.map(d => d.email).filter(Boolean)];
        }
      }
      setAllErrorEmails(errorEmails);
      
      const relevantTables = tables.filter(t => t.table_type === activeTab);
      console.log('Tabelas relevantes:', relevantTables);
      
      let allData: any[] = [];

      for (const table of relevantTables) {
        console.log(`Buscando dados da tabela ${table.table_name}...`);
        
        const { data, error } = await supabase
          .from(table.table_name)
          .select('*');

        if (error) {
          console.error(`Erro ao buscar dados da tabela ${table.table_name}:`, error);
          console.log('Detalhes do erro:', {
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          continue;
        }

        if (data) {
          console.log(`Dados encontrados na tabela ${table.table_name}:`, data.length, 'registros');
          allData = [...allData, ...data.map(item => ({
            ...item,
            originalTable: table.table_name
          }))];
        } else {
          console.log(`Nenhum dado encontrado na tabela ${table.table_name}`);
        }
      }

      console.log('Total de dados coletados:', allData.length);

      const processedStudents = allData.map(student => ({
        ...student,
        id: student.id || crypto.randomUUID(),
        data_de_nascimento: student.data_de_nascimento || '',
        data: student.data || new Date().toISOString(),
        status: activeTab,
        nome_completo: student.nome_completo || '',
        email: student.email || '',
        cpf: String(student.cpf || ''),
        rg: student.rg || '',
        orgao_expedidor: student.orgao_expedidor || '',
        endereco: student.endereco || '',
        numero_da_residencia: student.numero_da_residencia || '',
        complemento: student.complemento || '',
        bairro: student.bairro || '',
        cidade: student.cidade || '',
        estado: student.estado || '',
        cep: student.cep || '',
        celular: student.celular || '',
        cod_curso: student.cod_curso || '',
        cod_curso_2: student.cod_curso_2 || '',
        metodo_de_pagamento: student.metodo_de_pagamento || '',
        parcelas: student.parcelas || 0,
        motivo_do_erro: student.motivo_do_erro || '',
        verified: student.verified || false,
      }));

      console.log('Dados processados:', processedStudents.length, 'registros');
      setStudents(sortStudents(processedStudents));
      
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      if (error instanceof Error) {
        console.log('Detalhes do erro:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Não informado';
    console.log('Formatando data (valor original):', dateString);
    console.log('Tipo da data:', typeof dateString);
    
    try {
      if (typeof dateString === 'string' && dateString.includes('/')) {
        console.log('Data no formato dd/mm/yyyy detectada');
        const [day, month, year] = dateString.split('/').map(Number);
        console.log('Partes da data:', { day, month, year });
        
        if (year === 0) {
          console.log('Ano inválido (0000)');
          return 'Data inválida';
        }
        
        const date = new Date(year, month - 1, day);
        console.log('Data convertida:', date);
        return date.toLocaleDateString('pt-BR');
      }

      if (dateString.includes('/') && dateString.endsWith('0000')) {
        console.log('Data inválida: formato dd/mm/0000 detectado');
        return 'Data inválida';
      }
      
      console.log('Tentando converter string para data:', dateString);
      const date = new Date(dateString);
      console.log('Data após conversão:', date);
      
      if (isNaN(date.getTime())) {
        console.log('Data inválida: timestamp inválido');
        return 'Data inválida';
      }
      
      if (date.getFullYear() === 0) {
        console.log('Data inválida: ano 0000');
        return 'Data inválida';
      }
      
      const formattedDate = date.toLocaleDateString('pt-BR');
      console.log('Data formatada com sucesso:', formattedDate);
      return formattedDate;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      console.error('Stack trace:', error instanceof Error ? error.stack : '');
      return 'Data inválida';
    }
  };

  const handleResolveEnrollment = async (studentId: string) => {
    const student = students.find(s => s.id === studentId);
    if (!student?.originalTable) return;

    const successTable = tables.find(t => t.table_type === 'success')?.table_name;
    if (!successTable) return;

    try {
      await resolveEnrollment(studentId, student.originalTable, successTable);
      await fetchStudents();
      toast.success('Matrícula resolvida com sucesso');
    } catch (error) {
      console.error('Erro ao resolver matrícula:', error);
      toast.error('Erro ao resolver matrícula');
    }
  };

  const handleMarkAsError = async (studentId: string) => {
    const student = students.find(s => s.id === studentId);
    if (!student?.originalTable) return;

    const errorTable = tables.find(t => t.table_type === 'error')?.table_name;
    if (!errorTable) return;

    try {
      await markAsError(
        studentId,
        student.originalTable,
        errorTable,
        'Matrícula marcada com erro'
      );
      setActiveTab('error');
      await fetchStudents();
      toast.success('Matrícula marcada como erro');
    } catch (error) {
      console.error('Erro ao marcar matrícula com erro:', error);
      toast.error('Erro ao marcar matrícula como erro');
    }
  };

  const filteredStudents = sortStudents(students.filter(student => {
    const matchesSearch = searchTerm === '' ||
      student.nome_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(student.cpf || '').includes(searchTerm);

    const matchesVerification = verificationFilter === 'all' ||
      (verificationFilter === 'verified' && isVerified(student.id)) ||
      (verificationFilter === 'unverified' && !isVerified(student.id));

    return matchesSearch && matchesVerification;
  }));

  const totalPages = Math.ceil(filteredStudents.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const renderStudentDetails = (student: Student) => (
    <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-700">
      <div>
        <p className="text-sm font-medium text-gray-400">Documentos</p>
        <p className="mt-1 text-gray-300">CPF: {student.cpf || 'Não informado'}</p>
        <p className="text-gray-300">RG: {student.rg || 'Não informado'}</p>
        <p className="text-gray-300">Órgão Expedidor: {student.orgao_expedidor || 'Não informado'}</p>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-400">Endereço</p>
        <p className="mt-1 text-gray-300">
          {student.endereco ? `${student.endereco}${student.numero_da_residencia ? `, ${student.numero_da_residencia}` : ''}` : 'Não informado'}
        </p>
        <p className="text-gray-300">{student.complemento || 'Sem complemento'}</p>
        <p className="text-gray-300">{student.bairro || 'Bairro não informado'}</p>
        <p className="text-gray-300">
          {student.cidade && student.estado ? `${student.cidade} - ${student.estado}` : 'Cidade não informada'}
        </p>
        <p className="text-gray-300">CEP: {student.cep || 'Não informado'}</p>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-400">Informações Adicionais</p>
        <p className="mt-1 text-gray-300">Data de Nascimento: {formatDate(student.data_de_nascimento)}</p>
        <p className="text-gray-300">Telefone: {student.celular || 'Não informado'}</p>
        <p className="text-gray-300">Forma de Pagamento: {student.metodo_de_pagamento || 'Não informado'}</p>
        <p className="text-gray-300">Parcelas: {student.parcelas ? `${student.parcelas}x` : 'Não informado'}</p>
        <p className="text-gray-300">Data de Matrícula: {formatDate(student.data)}</p>
        {student.status === 'error' && student.motivo_do_erro && (
          <p className="text-red-400 mt-2">Motivo do erro: {student.motivo_do_erro}</p>
        )}
      </div>
    </div>
  );

  if (!user) {
    return <Login onLoginSuccess={() => {}} />;
  }

  return (
    <div className="flex min-h-screen bg-[#111318]">
      <Sidebar
        user={user}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
      />

      <button
        onClick={() => setMenuOpen(true)}
        className={`fixed top-4 left-4 z-20 p-2 text-white rounded-lg bg-[#26428C] ${menuOpen ? 'hidden' : 'block'}`}
      >
        <Menu className="w-6 h-6" />
      </button>

      <div className={`flex-1 transition-all duration-200 ${menuOpen ? 'ml-64' : 'ml-0'}`}>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-white">
              Painel de Gerenciamento de Matrículas
            </h1>
            <div className="flex gap-2">
              <Notifications />
              <button
                onClick={() => fetchStudents()}
                className="p-2 text-white rounded-lg hover:bg-opacity-80 transition-colors bg-[#26428C]"
                title="Atualizar dados"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={() => exportToCSV(students)}
                className="flex items-center px-4 py-2 text-white rounded-lg hover:bg-opacity-80 transition-colors bg-[#26428C]"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </button>
            </div>
          </div>

          <div className="mb-6 flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, email ou CPF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 text-white bg-[#1a1d23] bg-opacity-10"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value as VerificationFilter)}
                className="px-4 py-2 rounded-lg text-white bg-[#1a1d23] border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#26428C]"
              >
                <option value="all">Todos</option>
                <option value="verified">Verificados</option>
                <option value="unverified">Não Verificados</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                className="flex items-center px-4 py-2 rounded-lg text-white bg-[#1a1d23] border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#26428C]"
              >
                <option value="recent">Mais Recentes</option>
                <option value="oldest">Mais Antigos</option>
                <option value="alphabetical">Ordem Alfabética</option>
              </select>
            </div>
          </div>

          <div className="flex space-x-4 mb-6">
            <button
              onClick={() => setActiveTab('success')}
              className={`flex items-center px-4 py-2 rounded-lg text-white ${
                activeTab === 'success'
                  ? 'bg-[#26428C] bg-opacity-100'
                  : 'bg-[#1a1d23] bg-opacity-50 hover:bg-opacity-75'
              }`}
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Matrículas ({students.filter(s => s.status === 'success').length})
            </button>
            <button
              onClick={() => setActiveTab('error')}
              className={`flex items-center px-4 py-2 rounded-lg text-white ${
                activeTab === 'error'
                  ? 'bg-[#26428C] bg-opacity-100'
                  : 'bg-[#1a1d23] bg-opacity-50 hover:bg-opacity-75'
              }`}
            >
              <XCircle className="w-5 h-5 mr-2" />
              Erros de Matrícula ({students.filter(s => s.status === 'error').length})
            </button>
          </div>

          <div className="rounded-lg shadow-sm divide-y divide-gray-700 bg-[#1a1d23]">
            {loading ? (
              <div className="p-4 text-center text-gray-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Carregando dados...
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                Nenhum registro encontrado
              </div>
            ) : (
              <>
                {paginatedStudents.map((student) => (
                  <div 
                    id={`student-${student.id}`}
                    key={student.id}
                    className={`p-4 transition-colors ${
                      student.status === 'error' 
                        ? 'bg-red-900/20' 
                        : isVerified(student.id)
                          ? 'bg-emerald-400/5'
                          : student.email && allErrorEmails.includes(student.email)
                            ? 'bg-red-900/10'
                            : ''
                    }`}
                  >
                    <div className="flex items-center justify-between cursor-pointer"
                         onClick={() => setExpandedStudent(expandedStudent === student.id ? null : student.id)}>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-white">{student.nome_completo || 'Nome não informado'}</h3>
                            <p className="text-sm text-gray-400">{student.email || 'Email não informado'}</p>
                            <p className="text-sm text-gray-400">
                              {(() => {
                                console.log('Cursos disponíveis:', courses);
                                console.log('Código do curso do aluno:', student.cod_curso);
                                
                                const normalizedStudentCode = String(student.cod_curso).trim();
                                const mainCourse = courses.find(c => String(c.codigo).trim() === normalizedStudentCode);
                                
                                const normalizedStudentCode2 = student.cod_curso_2 ? String(student.cod_curso_2).trim() : null;
                                const secondCourse = normalizedStudentCode2 
                                  ? courses.find(c => String(c.codigo).trim() === normalizedStudentCode2)
                                  : null;

                                console.log('Curso principal encontrado:', mainCourse);
                                console.log('Curso secundário encontrado:', secondCourse);

                                return `Curso: ${student.cod_curso} - ${mainCourse?.nome || 'Não encontrado'}${
                                  student.cod_curso_2
                                    ? `, ${student.cod_curso_2} - ${secondCourse?.nome || 'Não encontrado'}`
                                    : ''
                                }`;
                              })()}
                            </p>
                            {student.status === 'success' && student.email && allErrorEmails.includes(student.email) && (
                              <p className="text-sm text-red-400">Email também presente na lista de erros</p>
                            )}
                            {student.originalTable && (
                              <p className="text-sm text-gray-400">Tabela: {student.originalTable}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {student.status === 'success' ? (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleVerification(student);
                                  }}
                                  className={`flex items-center px-3 py-1 rounded-md hover:bg-opacity-80 transition-colors ${
                                    isVerified(student.id)
                                      ? 'text-emerald-500 bg-emerald-500/10'
                                      : 'text-gray-400 bg-gray-700/50 hover:bg-gray-600/50'
                                  }`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  {isVerified(student.id) ? 'Verificado' : 'Verificar'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsError(student.id);
                                  }}
                                  className="flex items-center px-3 py-1 rounded-md hover:bg-opacity-80 transition-colors text-red-500 bg-red-500/10"
                                >
                                  <AlertTriangle className="w-4 h-4 mr-1" />
                                  Sinalizar erro
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResolveEnrollment(student.id);
                                }}
                                className="flex items-center px-3 py-1 rounded-md hover:bg-opacity-80 transition-colors text-white bg-[#26428C]"
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Resolver
                              </button>
                            )}
                            <ChevronDown 
                              className={`w-5 h-5 ml-4 text-gray-400 transform transition-transform ${
                                expandedStudent === student.id ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        </div>
                        {student.status === 'error' && student.motivo_do_erro && (
                          <p className="text-sm text-red-400 mt-1">{student.motivo_do_erro}</p>
                        )}
                      </div>
                    </div>
                    
                    {expandedStudent === student.id && renderStudentDetails(student)}
                  </div>
                ))}

                <div className="p-4 flex items-center justify-between border-t border-gray-700">
                  <div className="text-sm text-gray-400">
                    Mostrando {startIndex + 1} a {Math.min(startIndex + ITEMS_PER_PAGE, filteredStudents.length)} de {filteredStudents.length} registros
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 text-white rounded-lg hover:bg-[#26428C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          const nearCurrent = Math.abs(page - currentPage) <= 1;
                          return page === 1 || page === totalPages || nearCurrent;
                        })
                        .map((page, index, array) => (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <span className="text-gray-600 px-2">...</span>
                            )}
                            <button
                              onClick={() => handlePageChange(page)}
                              className={`min-w-[32px] h-8 flex items-center justify-center rounded-lg transition-colors ${
                                currentPage === page
                                  ? 'bg-[#26428C] text-white'
                                  : 'text-gray-400 hover:bg-[#26428C] hover:text-white'
                              }` }
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 text-white rounded-lg hover:bg-[#26428C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;